import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { makeClient } from '@/lib/api';
import { formatPhone, formatChatIdentity } from '@/lib/phone';
import { useT } from '@/lib/i18n';
import { ConversationDrawer } from '@/components/ConversationDrawer';
import { useProjects } from '@/components/ProjectSwitcher';
import { getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';

interface Instance { id: string; phone: string; nickname?: string }
interface Conv { chat_jid: string; name?: string; last_body?: string; last_direction?: string; last_at?: string; last_inbound_at?: string; unread: number }

export function Inbox() {
  const t = useT();
  const { data: instances } = useQuery({
    queryKey: ['instances'],
    queryFn: async (): Promise<Instance[]> => ((await makeClient().GET('/instances')).data as { data?: Instance[] })?.data ?? [],
  });
  const [instanceId, setInstanceId] = useState<string>(''); // '' = todos os números
  const [active, setActive] = useState<Conv | null>(null);
  const { data: projects = [] } = useProjects();
  const official = isOfficial(projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default));

  // Conversas UNIFICADAS por cliente (sem instance_id → através de todos os números).
  const { data: convs, isLoading } = useQuery({
    queryKey: ['conversations', instanceId],
    queryFn: async (): Promise<Conv[]> => {
      // instance_id é opcional no servidor; o tipo gerado do SDK está estrito (cast).
      const query = instanceId ? { instance_id: instanceId } : ({} as { instance_id: string });
      return ((await makeClient().GET('/conversations', { params: { query } })).data as { data?: Conv[] })?.data ?? [];
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('inbox.subtitle')}</p>
        <div className="w-full sm:w-56">
          <Select value={instanceId || 'all'} onValueChange={(v) => setInstanceId(v === 'all' ? '' : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('inbox.allNumbers')}</SelectItem>
              {(instances ?? []).map((i) => <SelectItem key={i.id} value={i.id}>{i.nickname || formatPhone(i.phone)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : (convs ?? []).length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <MessageSquare className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('inbox.empty')}</p>
            </div>
          ) : (
            <ul className="divide-y">
              {(convs ?? []).map((c) => {
                const ident = formatChatIdentity(c.chat_jid);
                return (
                  <li
                    key={c.chat_jid}
                    onClick={() => setActive(c)}
                    className="flex cursor-pointer items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted">
                      {c.last_direction === 'out' ? <ArrowUpRight className="size-4 text-muted-foreground" /> : <ArrowDownLeft className="size-4 text-primary" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex items-baseline gap-2 text-sm font-medium">
                        {c.name ? (
                          <>
                            <span className="truncate">{c.name}</span>
                            {ident.isPhone && <span className="shrink-0 text-xs font-normal text-muted-foreground">{ident.text}</span>}
                          </>
                        ) : (
                          <span className="truncate">{ident.text}</span>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{c.last_body || '—'}</p>
                    </div>
                    {c.unread > 0 && <Badge>{c.unread}</Badge>}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* O Inbox é atendimento: aqui o drawer ganha o compositor. Com "todos os
          números" selecionado, o instance_id vai vazio de propósito — a API reusa
          o número que já conversa com o contato (afinidade de conversa). */}
      <ConversationDrawer
        open={!!active}
        onOpenChange={(o) => { if (!o) setActive(null); }}
        chatJid={active?.chat_jid ?? null}
        name={active?.name}
        canSend
        instanceId={instanceId || undefined}
        official={official}
        lastInboundAt={active?.last_inbound_at}
      />
    </div>
  );
}
